/**
 * Poll Real-time Updates Hook
 * Manages WebSocket events for real-time poll updates
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { socketClient } from '../services/socket.client';
import { pollKeys } from './usePolls';
import type { Poll, PollUpdatedEvent, PollClosedEvent } from '../types/api.types';

/**
 * Hook to handle real-time poll updates via WebSocket
 * @param pollId - The poll ID to listen for updates
 */
export function usePollRealtime(pollId: string | undefined) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!pollId) return;

    // Check if socket is connected
    if (!socketClient.isConnected()) {
      console.warn('WebSocket not connected for poll real-time updates');
      return;
    }

    // Handle poll updated event
    const handlePollUpdated = (data: PollUpdatedEvent) => {
      if (data.pollId !== pollId) return;

      // Update poll in cache with new vote data
      queryClient.setQueryData<Poll>(pollKeys.poll(pollId), (old) => {
        if (!old) return old;

        return {
          ...old,
          totalVotes: data.totalVotes,
          options: old.options.map((opt) => {
            const updated = data.options.find((o) => o.id === opt.id);
            if (updated) {
              return {
                ...opt,
                voteCount: updated.voteCount,
                percentage: updated.percentage,
              };
            }
            return opt;
          }),
        };
      });

      // Also update results cache
      queryClient.setQueryData<Poll>(pollKeys.results(pollId), (old) => {
        if (!old) return old;

        return {
          ...old,
          totalVotes: data.totalVotes,
          options: old.options.map((opt) => {
            const updated = data.options.find((o) => o.id === opt.id);
            if (updated) {
              return {
                ...opt,
                voteCount: updated.voteCount,
                percentage: updated.percentage,
              };
            }
            return opt;
          }),
        };
      });
    };

    // Handle poll closed event
    const handlePollClosed = (data: PollClosedEvent) => {
      if (data.pollId !== pollId) return;

      // Update poll to mark as expired
      queryClient.setQueryData<Poll>(pollKeys.poll(pollId), (old) => {
        if (!old) return old;

        return {
          ...old,
          isExpired: true,
          expires_at: data.closedAt,
        };
      });

      // Also update results cache
      queryClient.setQueryData<Poll>(pollKeys.results(pollId), (old) => {
        if (!old) return old;

        return {
          ...old,
          isExpired: true,
          expires_at: data.closedAt,
        };
      });
    };

    // Register event listeners
    socketClient.onPollUpdated(handlePollUpdated);
    socketClient.onPollClosed(handlePollClosed);

    // Cleanup
    return () => {
      socketClient.offPollUpdated(handlePollUpdated);
      socketClient.offPollClosed(handlePollClosed);
    };
  }, [pollId, queryClient]);
}

/**
 * Hook to handle real-time poll updates for all polls in a channel/DM
 * This should be used in the channel/DM view to update all visible polls
 */
export function useChannelPollsRealtime(channelId?: string, dmThreadId?: string) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!channelId && !dmThreadId) return;

    // Check if socket is connected
    if (!socketClient.isConnected()) {
      console.warn('WebSocket not connected for channel polls real-time updates');
      return;
    }

    // Handle poll updated event for any poll
    const handlePollUpdated = (data: PollUpdatedEvent) => {
      // Update specific poll cache
      queryClient.setQueryData<Poll>(pollKeys.poll(data.pollId), (old) => {
        if (!old) return old;

        return {
          ...old,
          totalVotes: data.totalVotes,
          options: old.options.map((opt) => {
            const updated = data.options.find((o) => o.id === opt.id);
            if (updated) {
              return {
                ...opt,
                voteCount: updated.voteCount,
                percentage: updated.percentage,
              };
            }
            return opt;
          }),
        };
      });

      // Invalidate messages to refresh poll data in message list
      if (channelId) {
        queryClient.invalidateQueries({ queryKey: ['channelMessages', channelId] });
      }
      if (dmThreadId) {
        queryClient.invalidateQueries({ queryKey: ['dmMessages', dmThreadId] });
      }
    };

    // Handle poll closed event for any poll
    const handlePollClosed = (data: PollClosedEvent) => {
      // Update specific poll cache
      queryClient.setQueryData<Poll>(pollKeys.poll(data.pollId), (old) => {
        if (!old) return old;

        return {
          ...old,
          isExpired: true,
          expires_at: data.closedAt,
        };
      });

      // Invalidate messages to refresh poll data in message list
      if (channelId) {
        queryClient.invalidateQueries({ queryKey: ['channelMessages', channelId] });
      }
      if (dmThreadId) {
        queryClient.invalidateQueries({ queryKey: ['dmMessages', dmThreadId] });
      }
    };

    // Register event listeners
    socketClient.onPollUpdated(handlePollUpdated);
    socketClient.onPollClosed(handlePollClosed);

    // Cleanup
    return () => {
      socketClient.offPollUpdated(handlePollUpdated);
      socketClient.offPollClosed(handlePollClosed);
    };
  }, [channelId, dmThreadId, queryClient]);
}
